// "Join the waitlist" — rendered inside the fully-booked capacity notice.
// Captures contact for the guest's ATTEMPTED slot; the server sweep re-probes
// capacity and messages a first-come-first-served offer when space frees up
// (cancellation, expired hold, raised day cap).

import { useState } from "react";
import type { BookingWindow } from "../state/booking";
import { joinBookingWaitlist } from "../state/waitlist";

export function WaitlistPrompt({
  serviceId,
  window,
}: {
  serviceId: string;
  window: BookingWindow;
}) {
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [pending, setPending] = useState(false);
  const [done, setDone] = useState<null | "new" | "already">(null);
  const [error, setError] = useState<string | null>(null);

  if (done) {
    return (
      <div className="vt-waitlist vt-waitlist-done" role="status">
        ✓ You&rsquo;re on the waitlist — we&rsquo;ll message you the moment space
        opens up{done === "already" ? " (we updated your existing spot)" : ""}.
      </div>
    );
  }

  async function submit() {
    const e = email.trim();
    const p = phone.trim();
    if (!e && !p) {
      setError("Add an email or phone number so we can reach you.");
      return;
    }
    setPending(true);
    setError(null);
    const r = await joinBookingWaitlist({
      serviceId,
      window,
      name: name.trim() || null,
      email: e || null,
      phone: p || null,
    });
    setPending(false);
    if (!r.ok) {
      setError(
        r.reason === "invalid_email"
          ? "That email doesn't look right."
          : "Couldn't join the waitlist — please try again.",
      );
      return;
    }
    setDone(r.already ? "already" : "new");
  }

  return (
    <div className="vt-waitlist">
      {!open ? (
        <button type="button" className="vt-waitlist-cta" onClick={() => setOpen(true)}>
          Join the waitlist — we&rsquo;ll message you if space opens up →
        </button>
      ) : (
        <div className="vt-waitlist-form">
          <div className="vt-waitlist-fields">
            <input
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Name"
              autoComplete="name"
            />
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Email"
              autoComplete="email"
            />
            <input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="Phone (optional)"
              autoComplete="tel"
            />
          </div>
          <button
            type="button"
            className="vt-waitlist-submit"
            disabled={pending}
            onClick={() => void submit()}
          >
            {pending ? "Joining…" : "Join waitlist"}
          </button>
          {error && <div className="vt-waitlist-error">{error}</div>}
        </div>
      )}
    </div>
  );
}
